import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { fetchApiMessageHistory } from "../../helpers/api_messages_helper";

/** Mesma chave do ApiMessageHistoryCard — evita toast duplicado na sessão. */
const FAILED_TOAST_STORAGE_KEY = "brenvio:api_messages_failed_toasted";
const POLL_MS = 45000;
const HISTORY_HREF = "/settings/api-tokens?api_messages=failed";

function readToastedIds(): Set<number> {
  try {
    const raw = sessionStorage.getItem(FAILED_TOAST_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter((n): n is number => typeof n === "number" && n > 0)
    );
  } catch {
    return new Set();
  }
}

function rememberToastedIds(ids: Set<number>): void {
  const list = Array.from(ids).slice(-200);
  try {
    sessionStorage.setItem(FAILED_TOAST_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

/**
 * Toast global de falhas POST /messages — Carla vê fora da tela Tokens.
 * Na própria tela Tokens o card já notifica; aqui pulamos para não duplicar poll.
 */
const ApiMessageFailureToaster = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const toastedRef = useRef<Set<number>>(readToastedIds());
  const skipRef = useRef(pathname.startsWith("/settings/api-tokens"));
  skipRef.current = pathname.startsWith("/settings/api-tokens");

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      if (skipRef.current) return;
      try {
        const res = await fetchApiMessageHistory({
          source: "api",
          status: "failed",
          per_page: 5,
        });
        if (cancelled) return;
        const rows = res.data?.items || [];
        let changed = false;
        for (const row of rows) {
          if (row.status !== "failed") continue;
          if (toastedRef.current.has(row.id)) continue;
          toastedRef.current.add(row.id);
          changed = true;
          toast.error(
            <div>
              <div>
                {t("apiMessages.history.failedToast", {
                  email: row.to_email,
                  subject: row.subject,
                })}
              </div>
              <Link
                to={HISTORY_HREF}
                className="text-white text-decoration-underline small"
                onClick={() => toast.dismiss(`api-msg-failed-${row.id}`)}
              >
                {t("apiMessages.history.failedToastCta")}
              </Link>
            </div>,
            { toastId: `api-msg-failed-${row.id}` }
          );
        }
        if (changed) rememberToastedIds(toastedRef.current);
      } catch {
        // silencioso — não poluir UI operacional
      }
    };

    void poll();
    const id = window.setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [t]);

  return null;
};

export default ApiMessageFailureToaster;
